/**
 * @fileoverview Pure rules deciding which action (if any) a tracker cell
 * offers the student. Kept out of the components so they can be unit-tested.
 */

import type { StudentTrackerFinal, StudentTrackerLesson } from '../types'

export type CellAction = 'submit' | 'edit' | 'view'
export type DisabledReason = 'future_lesson'

export interface CellPlan {
  action: CellAction | null
  /** The action is shown but cannot be clicked (with a tooltip explaining why). */
  disabled: boolean
  disabledReason: DisabledReason | null
}

const NO_ACTION: CellPlan = { action: null, disabled: false, disabledReason: null }
const enabled = (action: CellAction): CellPlan => ({ action, disabled: false, disabledReason: null })

/** Deliverable: submit while pending, edit once submitted; nothing once the project is finished. */
export function deliverablePlan(lesson: StudentTrackerLesson, editable: boolean): CellPlan {
  if (lesson.deliverableActivities.length === 0 || !editable) return NO_ACTION
  return enabled(lesson.deliverableStatus === 'completed' ? 'edit' : 'submit')
}

/**
 * Self-assessment: submit/edit while the project is open. A lesson in the
 * future cannot be answered yet (same rule as the teacher tracker).
 */
export function selfAssessmentPlan(lesson: StudentTrackerLesson, editable: boolean, today: string): CellPlan {
  if (!lesson.hasAssessments || !editable) return NO_ACTION
  if (lesson.selfStatus === 'pending' && lesson.date > today) {
    return { action: 'submit', disabled: true, disabledReason: 'future_lesson' }
  }
  return enabled(lesson.selfStatus === 'completed' ? 'edit' : 'submit')
}

/** Teacher assessment: read-only view as soon as the teacher started scoring (also on finished projects). */
export function teacherAssessmentPlan(lesson: StudentTrackerLesson): CellPlan {
  if (!lesson.hasAssessments || lesson.teacherStatus === 'pending') return NO_ACTION
  return enabled('view')
}

/** Final self-assessment: only once the phase started and while the project is open. */
export function finalSelfAssessmentPlan(final: StudentTrackerFinal, editable: boolean): CellPlan {
  if (!final.present || !final.enabled || !editable) return NO_ACTION
  return enabled(final.selfStatus === 'completed' ? 'edit' : 'submit')
}
